import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { SKILL_LABELS, SKILL_ORDER, clbTone, FeedbackReport } from "@/lib/tcf";
import {
  ArrowLeft, ArrowRight, Volume2, Loader2, BookOpen, Headphones, PenLine, Mic,
  ClipboardCheck, ChevronDown, Target, CheckCircle2,
} from "lucide-react";

const SKILL_ICONS = { reading: BookOpen, listening: Headphones, writing: PenLine, speaking: Mic };

function speak(text) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "fr-FR"; u.rate = 0.95;
  const v = window.speechSynthesis.getVoices().find((x) => x.lang.startsWith("fr"));
  if (v) u.voice = v;
  window.speechSynthesis.speak(u);
}

export default function AssessmentRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [step, setStep] = useState(0);
  const [reading, setReading] = useState({});
  const [listening, setListening] = useState({});
  const [writing, setWriting] = useState({});
  const [speaking, setSpeaking] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const topRef = useRef(null);

  useEffect(() => {
    api.get(`/tcf/assessment/${id}`).then((r) => setA(r.data)).catch(() => { toast.error("Test introuvable"); navigate("/tests"); });
    return () => window.speechSynthesis?.cancel();
  }, [id, navigate]);

  const sections = useMemo(() => (a ? SKILL_ORDER.filter((s) => (a[s] || []).length > 0) : []), [a]);
  useEffect(() => { topRef.current?.scrollIntoView({ behavior: "smooth" }); }, [step]);

  if (!a) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tcf/assessment/${id}/submit`, { reading, listening, writing, speaking });
      setResult(data);
      window.speechSynthesis?.cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Soumission impossible.");
    } finally { setLoading(false); }
  };

  // ---------------- RESULT ----------------
  if (result) {
    const target = result.target_clb;
    return (
      <div className="max-w-3xl mx-auto" data-testid="assessment-result">
        <button onClick={() => navigate("/tests")} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Tests</button>

        <div className="rounded-3xl emerald-surface grain relative overflow-hidden p-8 text-center text-white">
          <ClipboardCheck className="h-9 w-9 mx-auto text-white/90" />
          <p className="mt-2 text-white/80 text-sm">{result.title} — estimated overall</p>
          <div className="mt-1 font-display text-6xl font-semibold" data-testid="result-overall-clb">CLB {result.overall_clb ?? "—"}</div>
          <p className="mt-2 text-white/85 text-sm">Weakest-skill basis (immigration standard). Target: CLB {target}.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {SKILL_ORDER.filter((s) => result.skills[s]).map((s) => {
            const v = result.skills[s];
            const Icon = SKILL_ICONS[s];
            return (
              <div key={s} className="rounded-2xl bg-card border border-border p-5" data-testid={`result-skill-${s}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" /> {SKILL_LABELS[s]}</span>
                  <span className={`font-display text-2xl font-semibold ${clbTone(v.clb, target)}`}>CLB {v.clb ?? "—"}</span>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {v.clb != null && !("correct" in v) ? <>Niveau {v.cefr}</> : null}
                  {"correct" in v ? <>{v.correct}/{v.total} bonnes ({v.pct}%) · ≈ {v.approx_699}/699</> : null}
                </div>
              </div>
            );
          })}
        </div>

        {result.plan?.length > 0 && (
          <div className="mt-6 rounded-2xl bg-card border border-border p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-primary" /> Your focus plan</h3>
            <div className="space-y-3">
              {result.plan.map((p, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="shrink-0 px-2 py-0.5 rounded-full bg-secondary font-bold text-xs">{SKILL_LABELS[p.skill]}</span>
                  <div><span className="text-muted-foreground">{p.tip}</span>{p.gap > 0 && <span className="text-destructive font-semibold"> (+{p.gap} CLB to target)</span>}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Writing / speaking detailed feedback */}
        {["writing", "speaking"].map((s) => {
          const details = result.skills[s]?.details;
          if (!details?.length) return null;
          return (
            <div key={s} className="mt-6">
              <h3 className="font-display text-xl font-semibold mb-3">{SKILL_LABELS[s]} — feedback</h3>
              <div className="space-y-3">
                {details.map((d) => {
                  const isOpen = openDetail === d.task_id;
                  return (
                    <div key={d.task_id} className="rounded-2xl bg-card border border-border overflow-hidden">
                      <button onClick={() => setOpenDetail(isOpen ? null : d.task_id)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`detail-toggle-${d.task_id}`}>
                        <span className="font-medium text-sm">{d.task_title}</span>
                        <span className="flex items-center gap-3">
                          <span className="font-display font-semibold text-primary">CLB {d.clb} · {d.cefr}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </span>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            <div className="p-4 border-t border-border"><FeedbackReport result={d} /></div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/score" className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">View score tracking</Link>
          <Link to="/tests" className="px-6 py-3 rounded-full border border-border font-semibold hover:bg-secondary transition-colors">Take another test</Link>
        </div>
      </div>
    );
  }

  // ---------------- LOADING (scoring) ----------------
  if (loading) {
    return (
      <div className="max-w-md mx-auto text-center py-24" data-testid="assessment-scoring">
        <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
        <h2 className="font-display text-2xl font-semibold mt-4">Scoring your test…</h2>
        <p className="text-muted-foreground mt-2">The AI examiner is grading your writing & speaking on the 5 official criteria. This can take up to a minute.</p>
      </div>
    );
  }

  // ---------------- RUNNER ----------------
  const skill = sections[step];
  const Icon = SKILL_ICONS[skill];
  const isLast = step === sections.length - 1;

  return (
    <div ref={topRef} data-testid="assessment-runner">
      <button onClick={() => navigate("/tests")} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Quitter</button>

      {/* Section progress */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto">
        {sections.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>
            {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : React.createElement(SKILL_ICONS[s], { className: "h-3.5 w-3.5" })}
            {SKILL_LABELS[s]}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><Icon className="h-5 w-5 text-white" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold">{SKILL_LABELS[skill]}</h1>
          <p className="text-sm text-muted-foreground">{a.title}</p>
        </div>
      </div>

      {/* Reading */}
      {skill === "reading" && (
        <div className="space-y-4" data-testid="section-reading">
          {a.reading.map((q, qi) => (
            <div key={q.id} className="rounded-2xl bg-card border border-border p-5">
              {q.context && <p className="text-muted-foreground italic mb-3 border-l-2 border-primary/40 pl-3">{q.context}</p>}
              <p className="font-medium mb-3">{qi + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <button key={oi} onClick={() => setReading((r) => ({ ...r, [q.id]: oi }))} data-testid={`rd-${q.id}-${oi}`}
                    className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-all ${reading[q.id] === oi ? "border-primary bg-accent" : "border-border hover:border-primary/40"}`}>{opt}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Listening */}
      {skill === "listening" && (
        <div className="space-y-4" data-testid="section-listening">
          {a.listening.map((q, qi) => (
            <div key={q.id} className="rounded-2xl bg-card border border-border p-5">
              <button onClick={() => speak(q.transcript)} data-testid={`play-${q.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full emerald-surface text-white text-sm font-semibold mb-3"><Volume2 className="h-4 w-4" /> Écouter l'audio</button>
              <p className="font-medium mb-3">{qi + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <button key={oi} onClick={() => setListening((r) => ({ ...r, [q.id]: oi }))} data-testid={`ls-${q.id}-${oi}`}
                    className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-all ${listening[q.id] === oi ? "border-primary bg-accent" : "border-border hover:border-primary/40"}`}>{opt}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Writing */}
      {skill === "writing" && (
        <div className="space-y-5" data-testid="section-writing">
          {a.writing.map((t) => {
            const val = writing[t.id] || "";
            const words = val.trim() ? val.trim().split(/\s+/).length : 0;
            return (
              <div key={t.id} className="rounded-2xl bg-card border border-border p-5">
                <div className="flex items-center gap-2 text-xs font-semibold mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{t.target}</span>
                  <span className="px-2 py-0.5 rounded-full bg-secondary">{t.min_words}-{t.max_words} mots · {t.minutes} min</span>
                </div>
                <h3 className="font-semibold">{t.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-3">{t.prompt}</p>
                <textarea data-testid={`wr-${t.id}`} value={val} onChange={(e) => setWriting((w) => ({ ...w, [t.id]: e.target.value }))} rows={6}
                  placeholder="Rédigez ici…" className="w-full p-4 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y" />
                <p className={`text-xs mt-1 ${words >= t.min_words ? "text-primary" : "text-muted-foreground"}`}>{words} / {t.min_words} mots</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Speaking */}
      {skill === "speaking" && (
        <div className="space-y-5" data-testid="section-speaking">
          <div className="rounded-xl bg-accent/60 border border-border p-3 text-sm text-muted-foreground">🎙️ Voice recording is coming soon. For now, type (or paste) what you would say — it's scored on the official speaking grid.</div>
          {a.speaking.map((t) => {
            const val = speaking[t.id] || "";
            const words = val.trim() ? val.trim().split(/\s+/).length : 0;
            return (
              <div key={t.id} className="rounded-2xl bg-card border border-border p-5">
                <div className="flex items-center gap-2 text-xs font-semibold mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{t.target}</span>
                  <span className="px-2 py-0.5 rounded-full bg-secondary">~{Math.round(t.seconds / 60)} min</span>
                </div>
                <h3 className="font-semibold">{t.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-3">{t.prompt}</p>
                <textarea data-testid={`sp-${t.id}`} value={val} onChange={(e) => setSpeaking((w) => ({ ...w, [t.id]: e.target.value }))} rows={5}
                  placeholder="Transcrivez votre réponse orale ici…" className="w-full p-4 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y" />
                <p className="text-xs mt-1 text-muted-foreground">{words} mots</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Nav */}
      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="assess-prev"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border font-semibold disabled:opacity-40 hover:bg-secondary transition-colors"><ArrowLeft className="h-4 w-4" /> Précédent</button>
        {isLast ? (
          <button onClick={submit} data-testid="assess-submit" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">Terminer & obtenir mon score</button>
        ) : (
          <button onClick={() => setStep((s) => s + 1)} data-testid="assess-next" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">Suivant <ArrowRight className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );
}
